import { useEffect, useRef } from 'react'
import { MantineProvider } from '@mantine/core'
import React from 'react'
import { getGloThemeManager } from './ThemeManager'
import { MantineDefaults } from './BitechMantineDefaults'
import { BTLoader } from './ReactLoader'
import { ThemeContext } from './ThemeContext'
import { useThemeCoreLoaded } from './useThemeCoreLoadedHook'
import { CSSError } from './CSSError'

const isSSR = () => typeof window === 'undefined' || !window.document

export interface IThemeCoreProviderProps {
  children: Array<React.ReactElement<any, any>> | React.ReactElement<any, any>
  coreNames: string | Array<string>
  loader?: React.ReactElement<any, any>
  loadedCallback?: (loaded: boolean) => void
  mantineTheme?: any
  enableMantine?: boolean
}

const ThemeCoreProviderControl = (props: IThemeCoreProviderProps) => {
  const [themeLoaded, themeFailed, resetCores, checkCores] = useThemeCoreLoaded(
    Array.isArray(props.coreNames)
      ? props.coreNames
      : props.coreNames.split(',')
  )

  const [mantineTheme, setMantineTheme] = React.useState(
    getGloThemeManager()?.theme?.includes('dark') ? 'dark' : 'light'
  )

  const coreOnLoad = (evt?: CustomEvent<any>) => {
    const newtheme = getGloThemeManager()?.theme?.includes('dark')
      ? 'dark'
      : 'light'
    if (newtheme != mantineTheme) {
      setMantineTheme(
        getGloThemeManager()?.theme?.includes('dark') ? 'dark' : 'light'
      )
    }
  }

  useEffect(() => {
    if (!isSSR()) {
      window.addEventListener('themecore.loaded', coreOnLoad)

      return () => {
        window.removeEventListener('themecore.loaded', coreOnLoad)
      }
    }
    coreOnLoad()
  }, [coreOnLoad])

  useEffect(() => {
    resetCores(
      Array.isArray(props.coreNames)
        ? props.coreNames
        : props.coreNames.split(','),
      []
    )
    checkCores()
  }, [props.coreNames])

  useEffect(() => {
    if (props.loadedCallback) {
      props.loadedCallback(themeLoaded)
    }
  }, [themeLoaded, themeFailed])

  useEffect(() => {
    if (!checkCores()) {
      setTimeout(() => {
        checkCores()
      }, 1000)
    }
  }, [])

  if (props.enableMantine) {
    return (
      <ThemeContext.Provider
        value={{
          AllLoaded: themeLoaded,
          mantineTheme: props.mantineTheme,
          coreNames: props.coreNames,
        }}
      >
        <MantineProvider
          withNormalizeCSS
          withGlobalStyles
          theme={{
            ...MantineDefaults,
            ...(props.mantineTheme ? props.mantineTheme : {}),
            colorScheme: props.mantineTheme ?? mantineTheme,
          }}
        >
          {themeLoaded
            ? React.Children.toArray(props.children)
            : props.loader ?? <BTLoader></BTLoader>}
          {themeFailed && <CSSError></CSSError>}
        </MantineProvider>
      </ThemeContext.Provider>
    )
  } else {
    return (
      <ThemeContext.Provider
        value={{
          AllLoaded: themeLoaded,
          mantineTheme: props.mantineTheme,
          coreNames: props.coreNames,
        }}
      >
        {themeLoaded
          ? React.Children.toArray(props.children)
          : props.loader ?? <BTLoader></BTLoader>}
        {themeFailed && <CSSError></CSSError>}
      </ThemeContext.Provider>
    )
  }
}

export const ThemeCoreProvider = React.memo(ThemeCoreProviderControl)
//export const ThemeCoreProvider = ThemeCoreProviderControl
